import type { ServiceRelationshipConfig, ServiceRelationshipView } from "@closer/shared";
import {
  createServiceRelationshipSchema,
  SERVICE_RELATIONSHIP_INITIATOR_NAMES,
  SERVICE_RELATIONSHIP_STATUS_NAMES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * HİZMET İLİŞKİSİ (CLOS-246 `[133]`).
 *
 * ### ⚠ Büro kendini YETKİLENDİREMİYOR
 *
 * Ekranda "hizmet ilişkisi kur" diye bir düğme yok; olan şey "talep gönder". Talep karşı
 * tarafın kararıyla aktifleşiyor ve ekran bunu durum rozetiyle söylüyor. Tek düğmeyle
 * aktifleşen bir yüzey, kartın çekirdek kuralını ekranda yalanlardı.
 *
 * ### ⚠ Karşı tarafın talebi İPTAL EDİLMİYOR
 *
 * "Talebi geri çek" yalnız BÜRONUN açtığı bekleyen talepte. Müvekkil tarafının açtığı
 * talebe verilecek cevap red — sunucunun sözü de bu ("iptal edilemez; reddedilebilir") ve
 * o karar müvekkil tarafının ekranında.
 *
 * ### ⚠ Aktif ilişki KAYIT ERİŞİMİ DEĞİL
 *
 * Bölüm bunu cümleyle yazıyor: ilişki hukuki hizmet kapsamıdır, sözleşme/not erişimi her
 * nesne için ayrıca verilir. Yazmasaydık "artık her şeyi görebiliyoruz" sanılırdı.
 */
export function ClientServiceRelationships({
  clientId,
  arsivde,
}: {
  clientId: string;
  /** ⚠ Sunucu arşivli müvekkille ilişki kurmayı reddediyor; ekran teklif de etmiyor. */
  arsivde: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);
  /** Satır başına seçilen gerekçe: aynı anda iki ilişkiye bakan kullanıcı karışmasın. */
  const [gerekce, setGerekce] = useState<Record<string, string>>({});

  const yetkili = can("client.service_scope");
  const form = useZodForm(createServiceRelationshipSchema);

  const anahtar = tenantKey(tenantId, "client-service-relationships", clientId);
  const iliskiler = useQuery({
    enabled: yetkili,
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ServiceRelationshipView[]>(`/api/clients/${clientId}/service-relationships`, {
          tenantId,
        })
      ).data,
  });

  const politika = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "service-policy"),
    queryFn: async () =>
      (await api<ServiceRelationshipConfig>("/api/clients/service-policy", { tenantId })).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const talepEt = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ServiceRelationshipView>(`/api/clients/${clientId}/service-relationships`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      setFormAcik(false);
      form.reset();
      await yenile();
      toast.success("Hizmet ilişkisi talebi gönderildi");
    },
    onError: (hata: unknown) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Talep gönderilemedi");
    },
  });

  /**
   * ⚠ Askı ve sona erdirme GEREKÇE İSTİYOR; dönüş istemiyor. Gerekçe sözlüğü konfigden
   * geliyor ve sunucu bilinmeyen anahtarı 422 ile reddediyor — ekran serbest metin
   * sunsaydı kullanıcı, sunucunun kabul etmeyeceği bir cümle yazardı.
   */
  const yasamDongusu = useMutation({
    mutationFn: (girdi: { relId: string; komut: "suspend" | "end" | "resume"; reason?: string }) =>
      api(`/api/clients/${clientId}/service-relationships/${girdi.relId}/${girdi.komut}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi.reason ? { reason: girdi.reason } : {}),
      }),
    onSuccess: async (_veri, girdi) => {
      setGerekce({});
      await yenile();
      /**
       * ⚠ Portföy sorumluluğu da tazeleniyor: askı ve sonlandırma, sunucuda o ilişkinin
       * AKTİF atamasını kapatıyor (`relationship_suspended` / `relationship_ended`).
       * Tazelemeseydik ekran, sunucunun az önce kapattığı sorumluluğu hâlâ "aktif" gösterir
       * ve kullanıcı kimin baktığı sorusuna yanlış cevap alırdı.
       */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "client-portfolio-assignments", clientId),
      });
      toast.success(
        girdi.komut === "suspend"
          ? "Hizmet ilişkisi askıya alındı"
          : girdi.komut === "resume"
            ? "Hizmet ilişkisi yeniden başladı"
            : "Hizmet ilişkisi sona erdi",
      );
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "İşlem tamamlanamadı"),
  });

  /** "Yeniden gönder" (CLOS-1140 §12.3): aynı davet, yeni e-posta işi; sunucu aralığı korur. */
  const yenidenGonder = useMutation({
    mutationFn: (relId: string) =>
      api(`/api/clients/${clientId}/service-relationships/${relId}/resend`, {
        method: "POST",
        tenantId,
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await yenile();
      toast.success("Davet yeniden gönderildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Davet yeniden gönderilemedi"),
  });

  const geriCek = useMutation({
    mutationFn: (relId: string) =>
      api(`/api/clients/${clientId}/service-relationships/${relId}/cancel`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Talep geri çekildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Talep geri çekilemedi"),
  });

  if (!yetkili) return null;

  const gonder = form.handleSubmit((girdi) => talepEt.mutate(girdi as Record<string, unknown>));
  const kapsamlar = politika.data?.scopeTypes ?? [];
  const kapsamAdi = (deger: string) => kapsamlar.find((k) => k.key === deger)?.label ?? deger;

  /**
   * Gerekçe anahtarı sözlükten okunuyor; sözlükte yoksa HAM anahtar gösteriliyor. Boş
   * bırakmak, konfigden kaldırılmış bir gerekçeyle askıya alınmış ilişkiyi "gerekçesiz"
   * gösterirdi — oysa gerekçe var, adı bilinmiyor.
   */
  const gerekceAdi = (deger: string | null, sozluk?: { key: string; label: string }[]) =>
    deger ? (sozluk?.find((g) => g.key === deger)?.label ?? deger) : "Bilinmeyen";

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="mr-auto text-base font-semibold text-fg-heading">Hizmet ilişkileri</h2>
          {!arsivde && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setFormAcik((a) => !a)}
            >
              {formAcik ? "Vazgeç" : "Hizmet talebi gönder"}
            </Button>
          )}
        </div>

        {/* ⚠ İlişki KAYIT ERİŞİMİ DEĞİL: kullanıcı bunu çıkarımla öğrenmiyor. */}
        <p className="text-xs text-fg-soft">
          Hizmet ilişkisi hukuki hizmetin kapsamını tanımlar; sözleşme, not ve kişisel veri erişimi
          her kayıt için ayrıca verilir.
        </p>
        {/*
          ⚠ ÖN KOŞUL FORMDAN ÖNCE SÖYLENİYOR — ölçülerek eklendi (4 Eylül 2026).

          Sunucu talebi ancak müvekkilin bir Müvekkil Alanı varsa kabul ediyor
          (`lib/serviceRelationships.ts`: alan bağı çözülemezse `409`, çünkü talebi ONAYLAYACAK
          dijital yüzey yok). Ekran bunu hiçbir yerde söylemiyordu: kullanıcı kapsamı, açıklamayı
          ve süreyi doldurup "Talebi gönder"e basıyor ve kuralı ANCAK BAŞARISIZ OLARAK öğreniyordu.

          Kural doğru; eksik olan onu önceden söylemekti. Bu, aynı gün sözleşme detayındaki
          Müvekkil Alanı bölümünde kapatılan kusurun aynısı: sunucuda duran bir ön koşulun
          ekranda sessiz kalması.
        */}
        <p className="text-xs text-fg-soft">
          Talep gönderebilmek için müvekkilin bir Müvekkil Alanı olmalı: onayı o alanın temsilcisi
          veriyor.
        </p>

        {arsivde && (
          <p className="text-xs text-fg-soft">
            Müvekkil arşivde: yeni hizmet talebi açılamaz. Yürüyen ilişkiler arşivlemeyle
            sonlanmıyor; askı ve sonlandırma komutları açık kalıyor.
          </p>
        )}

        {formAcik && !arsivde && (
          <form noValidate className="flex flex-col gap-2" onSubmit={gonder}>
            <FormField label="Hizmet kapsamı" error={form.formState.errors.scope?.message} required>
              <Select {...form.register("scope")}>
                <option value="">Seçiniz</option>
                {kapsamlar.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField
              label="Kapsam açıklaması"
              error={form.formState.errors.scopeDetail?.message}
              hint="Karşı tarafın neye onay verdiğini görmesi için."
            >
              <Textarea
                rows={2}
                {...form.register("scopeDetail", {
                  setValueAs: (deger: string) => (deger === "" ? undefined : deger),
                })}
              />
            </FormField>

            <FormField
              label="Süre (gün)"
              error={form.formState.errors.durationDays?.message}
              hint={
                politika.data
                  ? `Varsayılan ${politika.data.defaultDurationDays}, en çok ${politika.data.maxDurationDays} gün.`
                  : undefined
              }
            >
              <Input
                type="number"
                {...form.register("durationDays", {
                  setValueAs: (deger: string) => (deger === "" ? undefined : Number(deger)),
                })}
              />
            </FormField>

            <div className="flex justify-end">
              <Button type="submit" size="sm" loading={talepEt.isPending}>
                Talebi gönder
              </Button>
            </div>
          </form>
        )}

        {iliskiler.isPending ? (
          <div role="status" aria-label="Hizmet ilişkileri yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : iliskiler.error ? (
          <StateView
            state={resolveErrorState(iliskiler.error, { idempotent: true })}
            onRetry={() => void iliskiler.refetch()}
          />
        ) : (iliskiler.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">
            Bu müvekkille kurulmuş ya da talep edilmiş hizmet ilişkisi yok.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(iliskiler.data ?? []).map((iliski) => (
              <li
                key={iliski.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-fg-strong">{kapsamAdi(iliski.scope)}</span>
                  <Badge tone={iliski.status === "active" ? "success" : "neutral"}>
                    {SERVICE_RELATIONSHIP_STATUS_NAMES[iliski.status]}
                  </Badge>
                  <span className="text-xs text-fg-soft">
                    {SERVICE_RELATIONSHIP_INITIATOR_NAMES[iliski.initiatedBy]}
                  </span>
                  <span className="text-xs text-fg-soft">{iliski.durationDays} gün</span>
                </div>

                {iliski.scopeDetail ? (
                  <p className="text-sm text-fg">{iliski.scopeDetail}</p>
                ) : null}

                {/* ⚠ Karar bekleyen talepte SÜRE bilgisi: cevapsız kalan talep kendiliğinden kapanır. */}
                {iliski.status === "pending" ? (
                  <span className="text-xs text-fg-soft">
                    {new Date(iliski.requestExpiresAt).toLocaleDateString("tr-TR")} tarihine kadar
                    karar bekliyor
                  </span>
                ) : null}

                {/*
                  ⚠ Geri çekme yalnız BÜRONUN açtığı bekleyen talepte: karşı tarafın talebine
                  verilecek cevap red ve o karar onun ekranında.
                */}
                {/* Closer Müvekkil daveti (e-posta yolu): adres + gönderim izi + yeniden gönder (§12.3). */}
                {iliski.status === "pending" && iliski.addressedTo === "email" ? (
                  <span className="text-xs text-fg-soft" data-testid="davet-adresi">
                    Davet gönderildi: {iliski.targetEmail}
                    {iliski.renewedAt
                      ? ` · yeniden gönderildi ${new Date(iliski.renewedAt).toLocaleDateString("tr-TR")}`
                      : ""}
                  </span>
                ) : null}
                {iliski.status === "pending" && iliski.initiatedBy === "law_firm" ? (
                  <div className="flex justify-end gap-2">
                    {iliski.addressedTo === "email" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => yenidenGonder.mutate(iliski.id)}
                        loading={yenidenGonder.isPending}
                      >
                        Yeniden gönder
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => geriCek.mutate(iliski.id)}
                    >
                      Talebi geri çek
                    </Button>
                  </div>
                ) : null}

                {/*
                  ⚠ Askıdaki ilişkide DÖNÜŞ HAKKI askıyı koyanda (`resumeApproval=suspender`).
                  Askıyı müvekkil tarafı koyduysa ekran düğme değil AÇIKLAMA gösteriyor:
                  sunucunun reddedeceği bir eylemi kullanıcıya denettirmek olurdu.
                */}
                {iliski.status === "suspended" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-fg-soft">
                      {gerekceAdi(iliski.suspendReason, politika.data?.suspendReasons)} gerekçesiyle
                      askıda
                    </span>
                    {iliski.suspendedBy === "law_firm" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => yasamDongusu.mutate({ relId: iliski.id, komut: "resume" })}
                      >
                        Askıdan çıkar
                      </Button>
                    ) : (
                      <span className="text-xs text-fg-soft">
                        Askıyı müvekkil tarafı koydu; askıdan çıkarma da onun kararı.
                      </span>
                    )}
                  </div>
                ) : null}

                {/*
                  ⚠ Askı ve sonlandırma AYRI gerekçe sözlükleri kullanıyor; tek listede
                  birleştirilseydi iki sözlükte de bulunan "Diğer" anahtarı çakışır ve
                  kullanıcı hangi eylemin gerekçesini seçtiğini bilemezdi.
                */}
                {iliski.status === "active" ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Askı gerekçesi">
                      <Select
                        aria-label={`${kapsamAdi(iliski.scope)} askı gerekçesi`}
                        value={gerekce[`${iliski.id}-suspend`] ?? ""}
                        onChange={(olay) =>
                          setGerekce((onceki) => ({
                            ...onceki,
                            [`${iliski.id}-suspend`]: olay.target.value,
                          }))
                        }
                      >
                        <option value="">Seçiniz</option>
                        {(politika.data?.suspendReasons ?? []).map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!gerekce[`${iliski.id}-suspend`]}
                      onClick={() =>
                        yasamDongusu.mutate({
                          relId: iliski.id,
                          komut: "suspend",
                          reason: gerekce[`${iliski.id}-suspend`],
                        })
                      }
                    >
                      Askıya al
                    </Button>
                  </div>
                ) : null}

                {/* ⚠ Sonlandırma açık ilişkide; sona eren ilişki GERİ GELMİYOR. */}
                {iliski.status === "active" || iliski.status === "suspended" ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Sonlandırma gerekçesi">
                      <Select
                        aria-label={`${kapsamAdi(iliski.scope)} sonlandırma gerekçesi`}
                        value={gerekce[`${iliski.id}-end`] ?? ""}
                        onChange={(olay) =>
                          setGerekce((onceki) => ({
                            ...onceki,
                            [`${iliski.id}-end`]: olay.target.value,
                          }))
                        }
                      >
                        <option value="">Seçiniz</option>
                        {(politika.data?.endReasons ?? []).map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!gerekce[`${iliski.id}-end`]}
                      onClick={() =>
                        yasamDongusu.mutate({
                          relId: iliski.id,
                          komut: "end",
                          reason: gerekce[`${iliski.id}-end`],
                        })
                      }
                    >
                      Hizmeti sonlandır
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
